import React from 'react';
import Button from '../components/ui/Button';
import Navigation from '../components/Navigation';

/**
 * Tutoring trial page - allows users to select between Mandarin and Cantonese
 * for private 1-on-1 tutoring
 */
const TutoringTrialPage = () => {
  // Handle language selection for private tutoring
  const handleLanguageSelect = (language) => {
    const url = `https://book.mandarintutorhk.com/start/${language.toLowerCase()}-tutoring-trial`;
    window.open(url, '_blank');
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <Navigation />
      <div className="flex items-center justify-center px-4 py-8">
        <div className="max-w-4xl w-full">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-3xl font-bold mb-4">
            Book Your Private 1-on-1 Trial Lesson
          </h1>
          <div className="w-16 h-1 bg-purple-500 mx-auto mb-6"></div>
          <p className="text-gray-300 text-lg">
            HK$450 for one hour. Select the Chinese language you want to learn:
          </p>
        </div>

        {/* Language selection cards */}
        <div className="grid md:grid-cols-2 gap-8">
          {/* Mandarin Card */}
          <div className="bg-gray-800 bg-opacity-50 rounded-lg border border-gray-700 p-8 text-center hover:border-purple-500 hover:bg-purple-500 hover:bg-opacity-5 transition-all duration-300">
            <h2 className="text-2xl font-bold mb-4 relative">
              <span className="bg-gradient-to-r from-purple-400 via-white to-purple-400 bg-clip-text text-transparent animate-shimmer bg-200%">
                MANDARIN
              </span>
            </h2>
            
            <div className="mb-6">
              <p className="text-gray-300 text-lg mb-4">
                普通话 • Pǔtōnghuà
              </p>
              
              <p className="text-gray-400 text-sm mb-4">
                Most widely spoken Chinese language
              </p>
              
              <p className="text-purple-400 font-semibold">
                1 BILLION+
              </p>
              <p className="text-gray-400 text-sm">
                speakers
              </p>
            </div>
            
            <Button
              variant="primary"
              className="bg-purple-600 hover:bg-purple-700 w-full transition-colors"
              onClick={() => handleLanguageSelect('mandarin')}
            >
              Book Trial
            </Button>
          </div>

          {/* Cantonese Card */}
          <div className="bg-gray-800 bg-opacity-50 rounded-lg border border-gray-700 p-8 text-center hover:border-purple-500 hover:bg-purple-500 hover:bg-opacity-5 transition-all duration-300">
            <h2 className="text-2xl font-bold mb-4 relative">
              <span className="bg-gradient-to-r from-purple-400 via-white to-purple-400 bg-clip-text text-transparent animate-shimmer bg-200%">
                CANTONESE
              </span>
            </h2>
            
            <div className="mb-6">
              <p className="text-gray-300 text-lg mb-4">
                粵語 • Yuht Yúh
              </p>
              
              <p className="text-gray-400 text-sm mb-4">
                Language of Hong Kong, Macau and Guangdong
              </p>
              
              <p className="text-purple-400 font-semibold">
                80 MILLION+
              </p>
              <p className="text-gray-400 text-sm">
                speakers
              </p>
            </div>
            
            <Button
              variant="primary"
              className="bg-purple-600 hover:bg-purple-700 w-full transition-colors"
              onClick={() => handleLanguageSelect('cantonese')}
            >
              Book Trial
            </Button>
          </div>
        </div>

        {/* Contact CTAs */}
        <div className="mt-12 text-center">
          <p className="text-gray-400 mb-4">Have questions? We're here to help</p>
          
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-6">
            {/* WhatsApp Link */}
            <a 
              href="https://wa.me/85251776613" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-green-500 hover:text-green-400 transition-colors"
            >
              <svg 
                className="w-6 h-6" 
                fill="currentColor" 
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
              </svg>
              <span>+852-5177-6613</span>
            </a>

            {/* Separator */}
            <span className="text-gray-600 hidden md:inline">•</span>

            {/* Email Link */}
            <a 
              href="mailto:aileen@mandarintutorhk.com" 
              className="inline-flex items-center gap-2 text-purple-400 hover:text-purple-300 transition-colors"
            >
              <svg 
                className="w-6 h-6" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" 
                />
              </svg>
              <span>aileen@mandarintutorhk.com</span>
            </a>
          </div>
        </div>
        </div>
      </div>
    </div>
  );
};

export default TutoringTrialPage;